<template>
  <view-finder
    ref="finder"
    :state="finderState"
    :caption="caption"
    :has-shot="!!shot"
    :flashing="flashing"
    :guide="guideState"
  >
    <video ref="video" playsinline muted autoplay></video>
    <img v-if="shot" class="shot" :src="shot" alt="" />

    <template #overlay>
      <div v-if="finderState === 'live'" class="live"><span class="dot"></span>دوربین فعال است</div>
    </template>
  </view-finder>
</template>

<script>
import ViewFinder from "./ViewFinder.vue";
import { captureFrame } from "@/utils/media";
import { canUseCamera } from "@/utils/device";
import { createFramingWatch } from "@/utils/face";

/**
 * Live camera preview plus a single still capture.
 *
 * Owns exactly one MediaStream and stops its tracks in `beforeDestroy` — the previous
 * implementation leaked the camera between screens, leaving the indicator light on.
 *
 * Acquisition is driven by the `active` prop rather than happening on mount, because the
 * getUserMedia prompt must follow a user gesture: the parent shows the priming sheet,
 * and flips `active` when the user taps "allow".
 *
 * Audio is requested alongside video even though a photo needs none, so the user answers
 * one combined permission prompt here instead of a second one at the video step.
 */
export default {
  name: "CameraCapture",
  components: { ViewFinder },

  props: {
    /** Hold a live stream. Setting this false releases the camera. */
    active: { type: Boolean, default: false },
    /** A captured still to display instead of the live feed. */
    shot: { type: String, default: "" },
    /**
     * Check that a face is framed inside the guide oval, and report it.
     * Off by default so a caller that only wants a preview does not pay for detection.
     */
    guide: { type: Boolean, default: false },
  },

  data() {
    return {
      /** "idle" | "live" | "denied" */
      status: "idle",
      flashing: false,
      /** "searching" | "adjust" | "ok" — the latest framing verdict. */
      framing: "searching",
      framingHint: "",
      /** Framed steadily enough to act on, not just for a single frame. */
      framed: false,
    };
  },

  computed: {
    finderState() {
      if (this.shot) return "captured";
      return this.status === "live" ? "live" : "idle";
    },
    /**
     * Is the framing check supposed to be running?
     *
     * Every input it depends on is in this one expression, so the watcher below fires on
     * all of them — including `guide`, which the photo step flips off once it has handed
     * the shutter back. Keying the watcher on `finderState` alone left the detector
     * polling at 8 fps with its answer discarded.
     */
    tracking() {
      return this.guide && this.finderState === "live";
    },

    /** What the oval should look like; "off" leaves it exactly as it was before. */
    guideState() {
      if (!this.tracking) return "off";
      if (this.framed) return "ok";
      // A good frame that has not settled is not yet a green light — the button is still
      // disabled, and the ring must not say otherwise.
      return this.framing === "ok" ? "adjust" : this.framing;
    },
    caption() {
      if (this.shot) return "عکس ثبت شد";
      if (this.guideState === "off") return "صورت خود را داخل کادر قرار دهید";
      return this.framingHint;
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.acquire();
        else this.release();
      },
    },
    shot(value) {
      // While a still is on screen the live feed is hidden anyway; pausing it saves the
      // decode work and lets the preview resume cleanly on retake.
      const video = this.$refs.video;
      if (!video) return;
      if (value) video.pause();
      else if (this.stream) this.attach();
    },
    tracking(on) {
      if (on) this.startTracking();
      else this.stopTracking();
    },
  },

  created() {
    // Deliberately not in data(): a MediaStream must never be made reactive — Vue would
    // walk the whole object, and nothing renders from it directly. The framing watch is
    // excluded for the same reason.
    this.stream = null;
    this.pending = null;
    this.destroyed = false;
    this.watch = createFramingWatch({
      getVideo: () => this.$refs.video,
      getGeometry: () => this.measure(),
      onChange: this.onFraming,
      onUnavailable: () => this.$emit("unavailable"),
    });
  },

  mounted() {
    // Not an `immediate` watcher: those run inside initState, before `created()` has built
    // `this.watch`, so the very first call threw a TypeError on every mount.
    if (this.tracking) this.startTracking();
  },

  beforeDestroy() {
    this.destroyed = true;
    this.stopTracking();
    this.release();
    clearTimeout(this.flashTimer);
  },

  methods: {
    /**
     * Acquire the camera, at most once at a time.
     *
     * The in-flight guard matters: the parent flips `active` (which the watcher picks up)
     * and may also call this directly on a retry. Without it both calls see a null stream,
     * both open the camera, and the second `srcObject` assignment restarts loading on a
     * video element that had almost finished — leaving `videoWidth` at 0, and leaking the
     * first stream's tracks.
     */
    async acquire() {
      if (this.pending) return this.pending;
      if (this.stream) {
        this.attach();
        return null;
      }
      if (!canUseCamera()) {
        this.status = "denied";
        this.$emit("denied");
        return null;
      }

      this.pending = (async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: true,
          });
          if (this.destroyed || !this.active) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          this.stream = stream;
          this.status = "live";
          await this.attach();
          this.$emit("ready", this.stream);
        } catch (e) {
          this.status = "denied";
          this.$emit("denied", e);
        } finally {
          this.pending = null;
        }
      })();

      return this.pending;
    },

    /**
     * Attach the stream and wait until a frame actually exists.
     *
     * Resolving on `loadedmetadata` rather than immediately is what makes `ready` mean
     * "you can take a photo now". Emitting earlier enables the shutter while
     * `video.videoWidth` is still 0, and `captureFrame()` then silently returns null —
     * the button appears to do nothing.
     */
    attach() {
      const video = this.$refs.video;
      if (!video || !this.stream) return Promise.resolve();

      video.srcObject = this.stream;
      const playing = video.play();
      // Autoplay can be refused; the stream is still attached and the next user gesture
      // starts it, so there is nothing to report.
      if (playing && playing.catch) playing.catch(() => {});

      if (video.videoWidth) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => {
          video.removeEventListener("loadedmetadata", done);
          resolve();
        };
        video.addEventListener("loadedmetadata", done);
        // Never hang the gate on a metadata event that does not arrive.
        setTimeout(done, 4000);
      });
    },

    startTracking() {
      this.watch.start();
    },

    stopTracking() {
      this.watch.stop();
    },

    /**
     * Where the guide oval is, read fresh each tick.
     *
     * Not cached: the frame is `flex: 1`, so anything that changes the layout above it
     * moves the oval — the video step's challenge phrase arriving from the network is
     * exactly that, and a stale rectangle miscalibrates every verdict after it. Two
     * getBoundingClientRect calls at 8 fps cost nothing next to the inference.
     */
    measure() {
      const finder = this.$refs.finder;
      return finder ? finder.geometry() : null;
    },

    onFraming(state) {
      this.framing = state.status;
      this.framingHint = state.hint;
      // The parent needs the reason, not just the flag: it is what gets spoken.
      this.$emit("framing", state);
      if (state.framed !== this.framed) {
        this.framed = state.framed;
        this.$emit("framed", state.framed);
      }
    },

    release() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      const video = this.$refs.video;
      if (video) video.srcObject = null;
      if (this.status === "live") this.status = "idle";
    },

    /**
     * Take the photo. Called by the parent through a ref — it is an action, not state.
     * @returns {string|null} a JPEG data-URL, or null if no frame was ready.
     */
    capture() {
      const dataUrl = captureFrame(this.$refs.video);
      if (!dataUrl) return null;

      this.flashing = false;
      // Let the class drop before re-adding it, or the animation will not restart.
      this.$nextTick(() => {
        this.flashing = true;
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => {
          this.flashing = false;
        }, 520);
      });

      this.$emit("capture", dataUrl);
      return dataUrl;
    },
  },
};
</script>
